import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDateTime } from "@/lib/helpers";
import { LogIn, LogOut, Clock } from "lucide-react";
import { toast } from "sonner";

export default function TimeTracking() {
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [openEntry, setOpenEntry] = useState(null);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [sumRes, entriesRes] = await Promise.all([api.get("/time-entries/summary"), api.get("/time-entries")]);
      setSummary(sumRes.data);
      setEntries(entriesRes.data);
      setOpenEntry(entriesRes.data.find((e) => !e.clock_out) || null);
    } catch {
      toast.error("Could not load time entries");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const clockIn = async () => {
    setBusy(true);
    try {
      await api.post("/time-entries/clock-in", { notes });
      toast.success("Clocked in");
      setNotes("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not clock in");
    } finally {
      setBusy(false);
    }
  };

  const clockOut = async () => {
    setBusy(true);
    try {
      await api.post("/time-entries/clock-out", { notes });
      toast.success("Clocked out");
      setNotes("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not clock out");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Skeleton className="h-64 rounded-[var(--radius)]" />;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="time-tracking-page">
      <div className="rounded-[var(--radius)] border bg-card p-6 text-center space-y-4">
        <Clock className="h-10 w-10 mx-auto text-[hsl(var(--role-accent))]" />
        <p className="text-sm text-muted-foreground">{openEntry ? `Clocked in since ${formatDateTime(openEntry.clock_in)}` : "You are currently clocked out"}</p>
        <Textarea
          placeholder='Optional note, e.g. "Windshield cracked" or "Vehicle needs gas"'
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="max-w-md mx-auto"
          data-testid="time-entry-notes-input"
        />
        <Button
          size="lg"
          disabled={busy}
          onClick={openEntry ? clockOut : clockIn}
          className={openEntry ? "bg-destructive hover:bg-destructive/90 h-12 px-8" : "bg-[hsl(var(--role-accent))] hover:opacity-95 h-12 px-8"}
          data-testid={openEntry ? "time-tracking-clock-out-button" : "time-tracking-clock-in-button"}
        >
          {openEntry ? <LogOut className="h-5 w-5 mr-2" /> : <LogIn className="h-5 w-5 mr-2" />}
          {openEntry ? "Clock Out" : "Clock In"}
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="rounded-[var(--radius)] border bg-card p-5">
          <p className="text-xs text-muted-foreground">Total Hours Logged</p>
          <p className="text-2xl font-semibold font-heading">{summary?.total_hours || 0} hrs</p>
        </div>
        <div className="rounded-[var(--radius)] border bg-card p-5">
          <p className="text-xs text-muted-foreground">Pay Periods Tracked</p>
          <p className="text-2xl font-semibold font-heading">{summary?.weekly_hours?.length || 0}</p>
        </div>
      </div>

      {summary?.weekly_hours?.length > 0 && (
        <div className="rounded-[var(--radius)] border bg-card p-4 sm:p-5 space-y-2">
          <h2 className="font-semibold font-heading">Weekly Hours</h2>
          <div className="divide-y">
            {summary.weekly_hours.slice().reverse().map((w) => (
              <div key={w.period} className="flex items-center justify-between py-2 text-sm" data-testid="time-tracking-week-row">
                <span className="text-muted-foreground">{w.period}</span>
                <span className="font-medium">{w.hours} hrs</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-[var(--radius)] border bg-card p-4 sm:p-5 space-y-2">
        <h2 className="font-semibold font-heading">Recent Entries</h2>
        {entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">No entries yet.</p>
        ) : (
          <div className="divide-y max-h-80 overflow-y-auto">
            {entries.map((e) => (
              <div key={e.id} className="py-2" data-testid="time-tracking-entry-row">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{formatDateTime(e.clock_in)}</span>
                  <span className="text-sm text-muted-foreground">{e.total_hours ? `${e.total_hours} hrs` : "In progress"}</span>
                </div>
                {e.notes && <p className="text-xs text-muted-foreground mt-0.5">{e.notes}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
